import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import Debug from 'debug';
import store from '@/store';
import { injectable } from 'inversify';
import { createUpserter, mergeRightToLeft } from '@/app/shared/utils'
import { calculateEffectiveGrade, defaultExamResultState, ExamResultState, ExamAttempt,
  GradingMethod } from '../domain';
import { ExamResultsQuery, RemoteStateQueryPhase, ExamResult } from './exam-result-query';
import { calculateExamPercent } from '../domain/exam-calculations';

export const ExamResultStoreModuleSymbol = Symbol.for('ExamResultStoreModule');

const upserter = createUpserter(defaultExamResultState);

const debug = Debug('exam-result-store-module');

@injectable()
@Module({
  namespaced: true,
  name: 'exam-result',
  store,
  dynamic: true
})
export class ExamResultStoreModule extends VuexModule implements ExamResultsQuery {

  private remoteStateQueryPhase: RemoteStateQueryPhase = 'none';
  private localState: ExamResultState[] = []
  private remoteState: ExamResultState[] = []

  @Mutation
  public addAttempt({ id, attempt, gradingMethod}: { id: string, attempt: ExamAttempt, gradingMethod: GradingMethod}) {
    debug('add attempt: ', id, attempt, gradingMethod)

    this.localState = upserter(this.localState, id, (current) => ({
      id: current.id,
      effectiveGrading: gradingMethod,
      attempts: [ ...current.attempts, {
        ...attempt
      } ]
    }))

    debug('attempt added, new state: ', this.localState)
  }
  
  @Mutation
  public startRemoteQuerying() {
    this.remoteStateQueryPhase = 'loading';
    this.remoteState = [];
  }

  @Mutation
  public finishRemoteQuerying(results: ExamResultState[]) {
    this.remoteState = results;
    this.remoteStateQueryPhase = 'loaded';
  }

  get findExam() {

    const self = this
    return function (id: string) {

      const found = self.examResults.find(x => x.id === id)
      if (!found) {
        return false
      }

      return found
    }
  }

  get examResults() {

    debug('exam results', this.remoteState, this.localState)
    const merged = mergeRightToLeft(this.remoteState, this.localState, merge)
    debug('merged', merged)
    
    return merged.map(mapExam)

    function merge(remote: ExamResultState, local: ExamResultState) {
      const attempts = [ ...remote.attempts, ...local.attempts ]
      return {
        id: local.id,
        effectiveGrading: local.effectiveGrading,
        attempts
      }
    }

    function mapExam(exam: ExamResultState) {

      if (exam.effectiveGrading === 'unknown') {
        throw new Error(`Coding error: effective grading not set for exam ${exam.id}; ${JSON.stringify(exam)}`)
      }

      const effectiveAttempt = calculateEffectiveGrade(exam.attempts, exam.effectiveGrading)
      if (effectiveAttempt === false) {
        throw new Error(`Coding error: missing attempt for exam ${exam.id}`)
      }
      
      const result: ExamResult = {
        id: exam.id,
        score: {
          min: effectiveAttempt.score.min,
          max: effectiveAttempt.score.max,
          raw: effectiveAttempt.score.raw,
          percent: calculateExamPercent({ min: effectiveAttempt.score.min,
                                          max: effectiveAttempt.score.max,
                                          raw: effectiveAttempt.score.raw })
        },
        attemptCount: exam.attempts.length,
        passingScore: effectiveAttempt.passingScore,
        result: effectiveAttempt.result,
        durationSeconds: effectiveAttempt.durationSeconds,
        timestamp: effectiveAttempt.timestamp
      }

      return result
    }
  }

  get remotePhase() {
    return this.remoteStateQueryPhase
  }
}

export default getModule(ExamResultStoreModule);
